import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Order, OrderStatus, OrderShipmentSummary, ShipmentStatus, Workshop } from "../types";
import { orderService, workshopService, shippingService } from "../services/dataService";
import { costService } from "../services/costService";
import { formatCurrency, getImageUrl } from "../utils/formatters";
import WorkshopTransferModal from "../components/WorkshopTransferModal";
import AlertModal from "../components/AlertModal";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionContext";
import "./OrderQRDetail.css";

/* ---------- yardımcılar ---------- */

const STATUS_LABEL: Record<number, string> = {
  [OrderStatus.Atanmadi]: "Atanmadı",
  [OrderStatus.Islemde]: "İşlemde",
  [OrderStatus.IptalEdildi]: "İptal Edildi",
  [OrderStatus.Tamamlandi]: "Tamamlandı",
};
const UNIT_LABEL: Record<number, string> = { 1: "Adet", 2: "Metre", 3: "Takım", 7: "Pano" };

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
};
const fmtDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtQty = (n?: number) => (n ?? 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });

/** Termin: kaç gün kaldı / geçti — KPI rengi ve alt yazısı için */
const deadlineInfo = (deadline?: string | null, status?: OrderStatus) => {
  if (!deadline) return { tone: "", sub: "Termin girilmemiş" };
  const d = new Date(deadline); if (isNaN(d.getTime())) return { tone: "", sub: "" };
  if (status === OrderStatus.Tamamlandi) return { tone: "ok", sub: "Tamamlandı" };
  if (status === OrderStatus.IptalEdildi) return { tone: "", sub: "İptal" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  const days = Math.round((dd.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { tone: "danger", sub: `${-days} gün gecikti` };
  if (days === 0) return { tone: "warn", sub: "Bugün" };
  if (days <= 3) return { tone: "warn", sub: `${days} gün kaldı` };
  return { tone: "", sub: `${days} gün kaldı` };
};

/** Hedef atölyenin adından yeni durumu türet (eski sayfayla aynı iş kuralı) */
const statusForWorkshop = (name: string | undefined, current: OrderStatus | undefined, picked: boolean) => {
  const n = (name || "").toLowerCase();
  if (n.includes("biten") || n.includes("tamamlanan") || n.includes("tamamlandı")) return OrderStatus.Tamamlandi;
  if (n.includes("atanmamış")) return OrderStatus.Atanmadi;
  if (picked) return OrderStatus.Islemde;
  return current;
};

const sortWorkshops = (list: Workshop[]) =>
  [...list].sort((a, b) => {
    const oa = a.displayOrder ?? 999, ob = b.displayOrder ?? 999;
    return oa !== ob ? oa - ob : a.name.localeCompare(b.name, "tr");
  });

interface LogRow {
  logId: string; changeType: string; fieldName: string; oldValue?: string | null; newValue?: string | null;
  changedAt: string; changedByUserName?: string | null; changeDescription?: string;
}

/* ---------- ikonlar ---------- */
const I = {
  firm: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5" /></svg>,
  model: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.5 7.3 16 4l-2 2h-4L8 4 3.5 7.3 5 10l2-1v11h10V9l2 1z" /></svg>,
  route: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="3" /><circle cx="18" cy="5" r="3" /><path d="M9 19h6a3 3 0 0 0 0-6H9a3 3 0 0 1 0-6h6" /></svg>,
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  note: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v12l-4 4H4z" /><path d="M16 20v-4h4M8 9h8M8 13h5" /></svg>,
  tag: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12 12 20l-9-9V3h8z" /><circle cx="7.5" cy="7.5" r="1.2" /></svg>,
  img: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.8" /><path d="m21 17-5-5-7 7" /></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></svg>,
  invoice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4M9 12h6M9 16h6" /></svg>,
};

/**
 * Sipariş transfer sayfası — QR koddan, çoğunlukla mobilde açılır.
 * Önce ne olduğunu göster (kahraman kart + KPI), sonra tek büyük iş: atölye transferi
 * (yapışkan alt çubuk). Ayrıntılar aşağıda, kısa kartlar halinde.
 */
const OrderQRDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { user } = useAuth();
  const { has, hasModule } = usePermissions();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [ship, setShip] = useState<OrderShipmentSummary | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState("");
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [copied, setCopied] = useState(false);

  const [editNote, setEditNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editQty, setEditQty] = useState(false);
  const [qtyText, setQtyText] = useState("");
  const [savingQty, setSavingQty] = useState(false);

  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const say = (message: string, type: "info" | "success" | "warning" | "error" = "info") => setAlert({ isOpen: true, message, type });

  const canEdit = has("orders:update");
  // Transfer kendi izni; "orders:update" sipariş kartını düzeltme iznidir.
  const canTransfer = has("orders:transfer") || has("workshop-kanban:transfer");
  const isManager = user?.role === "Manager";

  /* ---------- veri ---------- */
  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const o = await orderService.getById(orderId);
      setOrder(o); setNoteText(o.note || ""); setQtyText(String(o.quantity ?? 0));
    } catch { setOrder(null); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // Günlük ve sevkiyat özeti bağımsız; hata sayfayı düşürmez
  useEffect(() => {
    if (!orderId) return;
    orderService.getOrderLogs(orderId).then((l) => setLogs(l as LogRow[])).catch(() => setLogs([]));
    if (hasModule("shipping") && has("shipping:view"))
      shippingService.orderSummary(orderId).then(setShip).catch(() => setShip(null));
  }, [orderId, order?.workshopId, order?.status, hasModule, has]);

  // Bağlı atölyeler (mevcut atölyeden geçilebilenler); atanmamışsa hepsi
  useEffect(() => {
    if (!order) return;
    let alive = true;
    (async () => {
      try {
        if (order.workshopId) {
          const conns = await workshopService.getConnections(order.workshopId);
          const targets = conns.map((c: any) => c.targetWorkshop).filter(Boolean) as Workshop[];
          if (alive) setWorkshops(sortWorkshops(targets));
          return;
        }
      } catch { /* bağlantı yoksa tüm liste */ }
      try { const all = await workshopService.getAll(); if (alive) setWorkshops(sortWorkshops(all)); }
      catch { if (alive) setWorkshops([]); }
    })();
    return () => { alive = false; };
  }, [order?.workshopId, order?.orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- türevler ---------- */
  const targets = useMemo(() => workshops.filter((w) => w.workshopId !== order?.workshopId), [workshops, order?.workshopId]);
  const target = targets.find((w) => w.workshopId === selectedWorkshopId);
  const dl = deadlineInfo(order?.deadline, order?.status);
  const isDone = order?.status === OrderStatus.Tamamlandi;
  const isCancelled = order?.status === OrderStatus.IptalEdildi;
  const shortId = order ? order.orderId.split("-")[0].toUpperCase() : "";
  const visibleLogs = useMemo(() => {
    const rows = logs.filter((l) => l.changeType === "WorkshopChanged" || l.changeType === "StatusChanged" || l.changeType === "OperatorChanged");
    return showAllLogs ? rows : rows.slice(0, 6);
  }, [logs, showAllLogs]);
  const lastWorkshopChange = logs.find((l) => l.changeType === "WorkshopChanged");

  /* ---------- eylemler ---------- */
  const copyId = async () => {
    if (!order) return;
    try { await navigator.clipboard.writeText(order.orderId); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { say("Kopyalanamadı.", "warning"); }
  };

  const saveQty = async () => {
    if (!order) return;
    const q = parseFloat(qtyText.replace(",", "."));
    if (isNaN(q) || q <= 0) return say("Miktar sıfırdan büyük olmalı.", "warning");
    setSavingQty(true);
    try { await orderService.update(order.orderId, { ...order, quantity: q }); setOrder({ ...order, quantity: q }); setEditQty(false); say("Miktar güncellendi.", "success"); }
    catch { say("Miktar güncellenemedi.", "error"); }
    finally { setSavingQty(false); }
  };

  const saveNote = async () => {
    if (!order) return;
    setSavingNote(true);
    try { await orderService.update(order.orderId, { ...order, note: noteText }); setOrder({ ...order, note: noteText }); setEditNote(false); say("Not kaydedildi.", "success"); }
    catch { say("Not kaydedilemedi.", "error"); }
    finally { setSavingNote(false); }
  };

  const handleTransferSave = async (costs: any[]) => {
    if (!order || !selectedWorkshopId) return;
    try {
      const newStatus = statusForWorkshop(target?.name, order.status, true);
      await orderService.assign(order.orderId, { workshopId: selectedWorkshopId, orderStatusId: newStatus });
      for (const c of costs || []) {
        await costService.addModelCost({
          modelId: order.modelId, orderId: order.orderId, costItemId: c.costItemId,
          quantity: c.quantityUsed, unit: c.unit, quantity2: c.quantity2, unit2: c.unit2,
          quantity3: c.quantity3, unit3: c.unit3, costUnitId3: c.costUnitId3,
          unitPrice: c.actualPrice, totalCost: c.totalCost, currency: c.currency,
          usage: order.workshop?.name || undefined, isActive: true,
        });
      }
      setShowTransferModal(false); setSelectedWorkshopId("");
      await loadOrder();
      say(`Sipariş ${target?.name || "yeni atölye"}ye transfer edildi.`, "success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      say("Transfer sırasında bir hata oluştu.", "error");
      throw err;
    }
  };

  /* ---------- kabuk (bileşen DEĞİL, fonksiyon: render içinde tanımlı bileşen her
     durum değişiminde Header/Sidebar'ı yeniden kurar, zil sayacı ve menü sıfırlanırdı) ---------- */
  const shell = (children: React.ReactNode) => (
    <>
      <Header onMenuClick={() => setSidebarOpen((v) => !v)} />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="qd-page"><div className="qd-inner">{children}</div></div>
    </>
  );

  if (loading) return shell(<div className="qd-state"><div className="qd-spin" /><p>Sipariş yükleniyor…</p></div>);
  if (!order) return shell(<div className="qd-state"><h2>Sipariş bulunamadı</h2><p>Bu sipariş silinmiş ya da bağlantı hatalı olabilir.</p></div>);

  const firm = order.firm, model = order.model;
  const images = [
    ...(model?.imageUrl ? [{ key: "model", url: model.imageUrl, label: "Model görseli", isModel: true }] : []),
    ...(order.images ?? []).map((im) => ({ key: im.orderImageId, url: getImageUrl(im.imageUrl), label: im.description || "Sipariş görseli", isModel: false })),
  ];

  return shell(
    <>
      {/* ===== Kahraman ===== */}
      <section className="qd-card qd-hero">
        <div className="qd-hero-top">
          <div style={{ minWidth: 0 }}>
            <div className="qd-hero-kicker">Sipariş · {fmtDate(order.acceptanceDate)}</div>
            <h1 className="qd-hero-title">{model?.modelCode || "Model kodu yok"}</h1>
            {model?.modelName && <p className="qd-hero-sub">{model.modelName}</p>}
            <div className="qd-hero-firm">{I.firm}<span>{firm?.firmName || "Firma belirtilmemiş"}{firm?.firmCode ? ` · ${firm.firmCode}` : ""}</span></div>
          </div>
          <button className="qd-idcopy" onClick={copyId} title={order.orderId} aria-label="Sipariş numarasını kopyala">
            {copied ? I.check : I.copy}<span>{copied ? "Kopyalandı" : shortId}</span>
          </button>
        </div>
        <div className="qd-hero-chips">
          <span className={`qd-chip s${order.status ?? 1}`}>{STATUS_LABEL[order.status ?? 1] || "Bilinmiyor"}</span>
          {order.priority && <span className="qd-chip glass plain">Öncelik: {order.priority}</span>}
          {order.isSample && <span className="qd-chip glass plain">Numune</span>}
          {order.orderTechnics && order.orderTechnics.length > 0 && <span className="qd-chip glass plain">{order.orderTechnics.length} teknik</span>}
        </div>
      </section>

      {/* ===== KPI ===== */}
      <div className="qd-kpis">
        <div className="qd-kpi">
          <div className="qd-kpi-label">Miktar</div>
          {editQty ? (
            <div className="qd-kpi-editrow">
              <input inputMode="decimal" value={qtyText} autoFocus onChange={(e) => setQtyText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveQty(); if (e.key === "Escape") setEditQty(false); }} disabled={savingQty} />
              <button className="qd-btn sm primary" onClick={saveQty} disabled={savingQty}>{savingQty ? "…" : "Kaydet"}</button>
            </div>
          ) : (
            <>
              <div className="qd-kpi-value">{fmtQty(order.quantity)} {UNIT_LABEL[order.unit ?? 1] || "Adet"}</div>
              <div className="qd-kpi-sub">{order.pieceCount ? `${order.pieceCount} parça / takım` : (order.width || order.height ? `${order.width ?? "—"} × ${order.height ?? "—"}` : " ")}</div>
              {canEdit && !isDone && <button className="qd-kpi-edit" onClick={() => { setQtyText(String(order.quantity ?? 0)); setEditQty(true); }} aria-label="Miktarı düzenle">{I.edit}</button>}
            </>
          )}
        </div>
        <div className={`qd-kpi ${dl.tone}`}>
          <div className="qd-kpi-label">Termin</div>
          <div className="qd-kpi-value">{order.deadline ? new Date(order.deadline).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "—"}</div>
          <div className="qd-kpi-sub">{dl.sub}</div>
        </div>
        <div className={`qd-kpi ${isDone ? "ok" : ""}`}>
          <div className="qd-kpi-label">Atölye</div>
          <div className="qd-kpi-value">{order.workshop?.name || "Atanmadı"}</div>
          <div className="qd-kpi-sub">{lastWorkshopChange ? `${fmtDate(lastWorkshopChange.changedAt)} itibarıyla` : (order.operator ? `${order.operator.firstName} ${order.operator.lastName}` : " ")}</div>
        </div>
      </div>

      {/* ===== Transfer ===== */}
      <section className="qd-card qd-transfer">
        <div className="qd-card-head"><h3>{I.route}Atölye Transferi</h3>{!isDone && !isCancelled && targets.length > 0 && <span className="qd-chip s2 plain">{targets.length} hedef</span>}</div>
        {isDone ? (
          <div className="qd-done">{I.check}<div>Bu sipariş tamamlandı.<small>{order.completionDate ? `Tamamlanma: ${fmtDateTime(order.completionDate)}` : "Transfer kapalı."}</small></div></div>
        ) : isCancelled ? (
          <div className="qd-ws-empty">Sipariş iptal edildi; transfer yapılamaz.</div>
        ) : !canTransfer ? (
          <div className="qd-ws-empty">Transfer için yetkiniz yok. Mevcut atölye: <b>{order.workshop?.name || "Atanmadı"}</b></div>
        ) : (
          <>
            <div className="qd-route">
              <div className="qd-route-box"><div className="lbl">Şu an</div><div className="val">{order.workshop?.name || "Atanmadı"}</div></div>
              <div className="qd-route-arrow">{I.arrow}</div>
              <div className={`qd-route-box next ${target ? "picked" : ""}`}><div className="lbl">Sonraki</div><div className={`val ${target ? "" : "empty"}`}>{target?.name || "Seçin"}</div></div>
            </div>
            {targets.length === 0 ? (
              <div className="qd-ws-empty">Bu atölyeden geçilebilecek bağlı atölye tanımlı değil. Atölye ve Teknikler ekranından bağlantı ekleyin.</div>
            ) : (
              <div className="qd-ws-list" role="radiogroup" aria-label="Hedef atölye">
                {targets.map((w) => {
                  const st = statusForWorkshop(w.name, order.status, true);
                  return (
                    <button key={w.workshopId} type="button" role="radio" aria-checked={selectedWorkshopId === w.workshopId}
                      className={`qd-ws ${selectedWorkshopId === w.workshopId ? "active" : ""}`}
                      onClick={() => setSelectedWorkshopId((cur) => (cur === w.workshopId ? "" : w.workshopId))}>
                      <span className="qd-ws-radio" aria-hidden="true" />
                      <span className="qd-ws-name">{w.name}{w.location && <span className="qd-ws-desc">{w.location}</span>}</span>
                      {st === OrderStatus.Tamamlandi && <span className="qd-ws-tag">Tamamlar</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* ===== Ayrıntılar ===== */}
      <div className="qd-grid2">
        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.firm}Firma</h3></div>
          <div className="qd-card-body"><dl className="qd-dl">
            <div><dt>Firma</dt><dd>{firm?.firmName || "—"}</dd></div>
            {firm?.firmCode && <div><dt>Kod</dt><dd>{firm.firmCode}</dd></div>}
            {firm?.contactPerson && <div><dt>Yetkili</dt><dd>{firm.contactPerson}</dd></div>}
            {firm?.phone && <div><dt>Telefon</dt><dd><a href={`tel:${firm.phone.replace(/\s/g, "")}`}>{firm.phone}</a></dd></div>}
            {firm?.email && <div><dt>E-posta</dt><dd><a href={`mailto:${firm.email}`}>{firm.email}</a></dd></div>}
          </dl></div>
        </section>

        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.model}Model</h3></div>
          <div className="qd-card-body"><dl className="qd-dl">
            <div><dt>Kod</dt><dd>{model?.modelCode || "—"}</dd></div>
            {model?.modelName && <div><dt>Ad</dt><dd>{model.modelName}</dd></div>}
            {model?.category && <div><dt>Kategori</dt><dd>{model.category}</dd></div>}
            {model?.season && <div><dt>Sezon</dt><dd>{model.season}</dd></div>}
            {model?.color && <div><dt>Renk</dt><dd>{model.color}</dd></div>}
            {model?.size && <div><dt>Beden</dt><dd>{model.size}</dd></div>}
            {(model as any)?.fabric && <div><dt>Kumaş</dt><dd>{(model as any).fabric}</dd></div>}
            {isManager && order.price ? <div><dt>Birim fiyat</dt><dd className="money">{formatCurrency(order.price, order.priceCurrency)}</dd></div> : null}
            {isManager && order.price && order.quantity ? <div><dt>Toplam</dt><dd className="money">{formatCurrency(order.price * order.quantity, order.priceCurrency)}</dd></div> : null}
          </dl>
          {model?.description && <p className="qd-note" style={{ marginTop: "0.6rem", color: "var(--qd-muted)", fontSize: "0.85rem" }}>{model.description}</p>}
          </div>
        </section>

        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.cal}Tarihler</h3></div>
          <div className="qd-card-body"><dl className="qd-dl">
            <div><dt>Kabul</dt><dd>{fmtDate(order.acceptanceDate)}</dd></div>
            <div><dt>Termin</dt><dd style={dl.tone === "danger" ? { color: "var(--qd-danger)" } : undefined}>{fmtDate(order.deadline)}{order.deadline && dl.sub ? ` · ${dl.sub}` : ""}</dd></div>
            {order.completionDate && <div><dt>Tamamlanma</dt><dd>{fmtDateTime(order.completionDate)}</dd></div>}
            {order.modelistUser && <div><dt>Modelist</dt><dd>{order.modelistUser.firstName} {order.modelistUser.lastName}</dd></div>}
          </dl></div>
        </section>

        <section className="qd-card">
          <div className="qd-card-head">
            <h3>{I.note}Not</h3>
            {canEdit && !editNote && <button className="qd-btn sm ghost" onClick={() => setEditNote(true)}>{I.edit}{order.note ? "Düzenle" : "Not ekle"}</button>}
          </div>
          <div className="qd-card-body">
            {editNote ? (
              <>
                <textarea className="qd-textarea" value={noteText} maxLength={500} autoFocus disabled={savingNote} placeholder="Atölye için not…" onChange={(e) => setNoteText(e.target.value)} />
                <div className="qd-inline-actions">
                  <button className="qd-btn sm" onClick={() => { setNoteText(order.note || ""); setEditNote(false); }} disabled={savingNote}>Vazgeç</button>
                  <button className="qd-btn sm primary" onClick={saveNote} disabled={savingNote}>{savingNote ? "Kaydediliyor…" : "Kaydet"}</button>
                </div>
              </>
            ) : (
              <p className={`qd-note ${order.note ? "" : "empty"}`}>{order.note || "Not eklenmemiş."}</p>
            )}
          </div>
        </section>
      </div>

      {order.orderTechnics && order.orderTechnics.length > 0 && (
        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.tag}Teknikler</h3></div>
          <div className="qd-card-body"><div className="qd-tags">{order.orderTechnics.map((ot, i) => <span key={ot.orderTechnicId || i} className="qd-tag">{ot.technic?.name || "—"}</span>)}</div></div>
        </section>
      )}

      {images.length > 0 && (
        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.img}Görseller</h3><span className="qd-chip s2 plain">{images.length}</span></div>
          <div className="qd-card-body"><div className="qd-images">
            {images.map((im) => (
              <div key={im.key} className={`qd-img ${im.isModel ? "model" : ""}`} onClick={() => setLightbox(im.url)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setLightbox(im.url)}>
                <img src={im.url} alt={im.label} loading="lazy" /><span>{im.label}</span>
              </div>
            ))}
          </div></div>
        </section>
      )}

      {ship && (
        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.truck}Sevkiyat</h3><span className={`qd-chip plain ${ship.remaining <= 0.0005 ? "s4" : "s2"}`}>{fmtQty(ship.shipped)} / {fmtQty(ship.quantity)}</span></div>
          <div className="qd-card-body">
            <div className={`qd-progress ${ship.remaining <= 0.0005 ? "done" : ""}`}><span style={{ width: `${ship.quantity > 0 ? Math.min(100, (ship.shipped / ship.quantity) * 100) : 0}%` }} /></div>
            {ship.shipments.length === 0 ? <p className="qd-note empty" style={{ fontSize: "0.82rem" }}>Henüz sevkiyat yok.</p> : ship.shipments.slice(0, 4).map((s) => (
              <div key={s.shipmentId} className="qd-shiprow">
                <span><b>{s.number}</b> · {fmtDate(s.shippedAt || s.shipmentDate)}</span>
                <span className={`qd-chip plain s${s.status === ShipmentStatus.Delivered ? 4 : s.status === ShipmentStatus.Cancelled ? 3 : s.status === ShipmentStatus.Shipped ? 2 : 1}`}>
                  {s.status === ShipmentStatus.Delivered ? "Teslim" : s.status === ShipmentStatus.Cancelled ? "İptal" : s.status === ShipmentStatus.Shipped ? "Yolda" : "Taslak"} · {fmtQty(s.totalQuantity)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {(order.invoice || order.invoiceNumber) && (
        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.invoice}Fatura</h3></div>
          <div className="qd-card-body"><dl className="qd-dl">
            {order.invoice && <div><dt>Fatura</dt><dd>{order.invoice}</dd></div>}
            {order.invoiceNumber && <div><dt>Fatura no</dt><dd>{order.invoiceNumber}</dd></div>}
          </dl></div>
        </section>
      )}

      {visibleLogs.length > 0 && (
        <section className="qd-card">
          <div className="qd-card-head"><h3>{I.clock}Geçmiş</h3>{logs.length > 6 && <button className="qd-btn sm ghost" onClick={() => setShowAllLogs((v) => !v)}>{showAllLogs ? "Kısalt" : "Tümü"}</button>}</div>
          <div className="qd-card-body"><ul className="qd-timeline">
            {visibleLogs.map((l) => (
              <li key={l.logId} className={`qd-tl ${l.changeType === "StatusChanged" ? "status" : l.changeType === "OperatorChanged" ? "user" : ""}`}>
                <div className="qd-tl-main">
                  {l.changeType === "WorkshopChanged" ? "Atölye" : l.changeType === "StatusChanged" ? "Durum" : "Sorumlu"}:
                  {" "}{l.oldValue || "—"}<span className="arrow">→</span>{l.newValue || "—"}
                </div>
                <div className="qd-tl-meta">{fmtDateTime(l.changedAt)}{l.changedByUserName ? ` · ${l.changedByUserName}` : ""}</div>
              </li>
            ))}
          </ul></div>
        </section>
      )}

      <div className="qd-foot">Oluşturma {fmtDateTime(order.createdAt)}{order.updatedAt ? ` · Güncelleme ${fmtDateTime(order.updatedAt)}` : ""}</div>

      {/* ===== Yapışkan aksiyon çubuğu ===== */}
      {canTransfer && !isDone && !isCancelled && (
        <div className="qd-actionbar">
          <div className="qd-actionbar-inner">
            <div className="qd-actionbar-text">
              {target ? <><b>{order.workshop?.name || "Atanmadı"} → {target.name}</b>{statusForWorkshop(target.name, order.status, true) === OrderStatus.Tamamlandi ? "Sipariş tamamlanacak" : "Atölye değişecek"}</> : <><b>Hedef atölye seçin</b>Transfer için yukarıdan bir atölye işaretleyin</>}
            </div>
            <button className="qd-btn primary" disabled={!selectedWorkshopId} onClick={() => setShowTransferModal(true)}>{I.arrow}Transfer Et</button>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="qd-lightbox" onClick={() => setLightbox(null)}>
          <button aria-label="Kapat" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Görsel" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {showTransferModal && selectedWorkshopId && (
        <WorkshopTransferModal
          isOpen={showTransferModal}
          orderId={order.orderId}
          orderQuantity={order.quantity}
          oldWorkshopId={order.workshopId || null}
          oldWorkshopName={order.workshop?.name || "Atanmadı"}
          newWorkshopId={selectedWorkshopId}
          newWorkshopName={target?.name || "Yeni Atölye"}
          currentUser={user || undefined}
          onClose={() => setShowTransferModal(false)}
          onSave={handleTransferSave}
        />
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, isOpen: false })} />
    </>,
  );
};

export default OrderQRDetail;
